import { Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { TRANSCRIPTS_CATEGORIES, SELECTED_TRANSCRIPT_LOCAL_STORAGE_KEY } from '../../constants';
import { Transcript } from '../../interfaces/TranscriptsInterface';
import { getTranscriptsFromAllBuckets } from '../../services/transcriptsService';
import useFetchData from '../hooks/useFetchData';
import useSchools from '../hooks/useSchools';
import { checkForNextPage } from './utilityFunctions';
import axios from 'axios';
import Loader from '../reusable/Loader';

function TranscriptSelectionLayout() {

  const { transcriptSelectionSchool } = useSchools();

  const [previewTranscriptIndex, setPreviewTranscriptIndex] = useState(Number(localStorage.getItem(SELECTED_TRANSCRIPT_LOCAL_STORAGE_KEY)) || 0);

  const [detailsTranscripts, setDetailsTranscripts] = useState([]);
  const [detailsCategory, setDetailsCategory] = useState('');
  const [detailsLinks, setDetailsLinks] = useState({ hasPrevousPage: false, hasNextPage: false });
  
  const {isPending, error, data: selectedTranscriptsSummary, setData: setSelectedTranscriptsSummary, refreshData} = useFetchData({
    fetchData: async (eoaId: string, access_token: string) => await fetchAllSelectedTranscripts(eoaId, access_token, transcriptSelectionSchool),
    errorMessage: 'Error occurred while fetching selected transcripts data!',
    options: {
      dependencies: [transcriptSelectionSchool],
      areDependenciesRequired: true
    },
    initialState: {
      is_finalized: false,
      selection_counts: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 0,
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 0,
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 0,
      },
      selectedTranscriptsByCategory: {
        [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: [],
        [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: [],
        [TRANSCRIPTS_CATEGORIES.STRUGGLING]: [],
      }
    }
  });
  
  const fetchAllSelectedTranscripts = async (eoaId: string, access_token: string, transcriptSelectionSchool: string) => {
    let page = 0;
  
    const allSelectedTranscriptsByCategory: Record<keyof typeof TRANSCRIPTS_CATEGORIES, Transcript[]> = {
      [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: [],
      [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: [],
      [TRANSCRIPTS_CATEGORIES.STRUGGLING]: [],
    };
  
    let isThereNextPage = true;
    let is_finalized = false;
    let selection_counts = {
      [TRANSCRIPTS_CATEGORIES.RIGOROUS_ACADEMIC]: 0,
      [TRANSCRIPTS_CATEGORIES.ON_THE_CUSP]: 0,
      [TRANSCRIPTS_CATEGORIES.STRUGGLING]: 0,
    };
  
    try {
      while (isThereNextPage) {
        const result = await getTranscriptsFromAllBuckets(eoaId, access_token, transcriptSelectionSchool, page, true);
  
        if (page === 0) {
          is_finalized = result.data.is_finalized;
          selection_counts = result.data.selection_counts;
        }
    
        (Object.values(TRANSCRIPTS_CATEGORIES) as (keyof typeof allSelectedTranscriptsByCategory)[])
          .forEach(category => { allSelectedTranscriptsByCategory[category].push(...(result.data.transcripts[category] || [])); });
    
        const linkHeader = result.headers?.link;
        isThereNextPage = checkForNextPage(linkHeader);
        page += 1;
      }
  
      return {
        data: {
          is_finalized,
          selection_counts,
          selectedTranscriptsByCategory: allSelectedTranscriptsByCategory,
        }
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response && error.response.status === 404) {
        return  {
          data: {
            is_finalized,
            selection_counts,
            selectedTranscriptsByCategory: allSelectedTranscriptsByCategory,
          }
        };
      } else {
        throw new Error('Unexpected error occurred!');
      }
    }
  };
  
  useEffect(() => {
    if (!localStorage.getItem(SELECTED_TRANSCRIPT_LOCAL_STORAGE_KEY)) localStorage.setItem(SELECTED_TRANSCRIPT_LOCAL_STORAGE_KEY, previewTranscriptIndex.toString());
  }, []);

  const handleUpdatePreviewTranscriptIndex = (newIndex: number) => {
    setPreviewTranscriptIndex(newIndex);
    localStorage.setItem(SELECTED_TRANSCRIPT_LOCAL_STORAGE_KEY, newIndex.toString());
  };

  const selectedTranscriptsSummaryResponse = {
    selectedTranscriptsError: error, 
    selectedTranscriptsSummary,
    setSelectedTranscriptsSummary
  };

  const contextValues = {
    previewTranscriptIndex,
    setPreviewTranscriptIndex: handleUpdatePreviewTranscriptIndex,
    detailsTranscripts,
    updateDetailsTranscripts: setDetailsTranscripts,
    detailsCategory,
    updateDetailsCategory: setDetailsCategory,
    detailsLinks,
    updateDetailsLinks: setDetailsLinks,
    selectedTranscriptsSummaryResponse,
    is_finalized: selectedTranscriptsSummary.is_finalized,
    selectionCounts: selectedTranscriptsSummary.selection_counts,
    refreshData
  };

  return isPending ? <Loader/> : <Outlet context={contextValues} />;
}

export default TranscriptSelectionLayout;